import React, { useContext, useState, useEffect } from 'react';
import ModalContext from '../../../contexts/ModalContext';
import {
  ModalContainer,
  Title,
  Subtitle,
  TableTitle,
  Table,
  Th,
  Td,
  ItemRow,
  MotivoInelegibilidade,
  DescricaoRiscada,
  BadgesRow,
  BadgePendencia,
  ValorFixo,
  Legenda,
  LegendaItem,
  LegendaDot,
  CheckboxInput,
  QtdInput,
  ValorInput,
  GradeRow,
  GradeCell,
  GradeTable,
  GradeVariantRow,
  GradeVariantCell,
  GradeTotal,
  ActionsContainer,
  CancelButton,
  ConfirmButton
} from './style';
import { useNavigate } from 'react-router-dom';
import ApiService from '../../../services/ApiService';
import { stripHtml } from '../../../utils/stripHtml';
import { FeedbackContext } from '../../../contexts/FeedbackContext';

// O DTO do fiscal valida no máximo 4 casas decimais em valorUnitario/valorTotal.
const round4 = (value: number) => Math.round(value * 10000) / 10000;

// Chave estável de uma variante da grade — casa com o par (collectionColorId, size) do backend.
const varianteKey = (collectionColorId: number, size: string) => `${collectionColorId}:${size}`;

const distribuirSaldoNaGrade = (grade: any[], disponivel: number): Record<string, string> => {
  let restante = Number(disponivel) || 0;
  const sugestao: Record<string, string> = {};
  for (const g of grade) {
    const quantidade = Math.max(0, Math.min(Number(g.quantidade) || 0, restante));
    sugestao[varianteKey(g.collectionColorId, g.size)] = String(quantidade);
    restante -= quantidade;
  }
  return sugestao;
};

const GenerateOrderInvoiceModal: React.FC = () => {
  const { selectedSteelOrder, setModalActive } = useContext(ModalContext);
  const { setFeedback } = useContext(FeedbackContext);
  const navigate = useNavigate();

  const [items, setItems] = useState<any[]>([]);

  useEffect(() => {
    const fetchEligibleItems = async () => {
      if (selectedSteelOrder) {
        try {
          const { data } = await ApiService.fiscal.getEligibleNfeItems(selectedSteelOrder.id);
          if (data && Array.isArray(data)) {
            setItems(data.map((item: any) => {
              const faturavel = item.elegivel !== false && item.totalmenteFaturado !== true;
              const grade = (item.grade ?? []) as any[];
              return {
                ...item,
                selected: faturavel,
                qtdNF: faturavel && grade.length === 0 ? String(item.quantidadeDisponivel ?? '') : '',
                valorNF: item.valorUnitarioSugerido != null ? String(item.valorUnitarioSugerido) : '',
                varianteQtds: (faturavel
                  ? distribuirSaldoNaGrade(grade, item.quantidadeDisponivel)
                  : {}) as Record<string, string>,
              };
            }));
          }
        } catch (error) {
          console.error('Erro ao buscar itens elegíveis do fiscal:', error);
        }
      }
    };
    fetchEligibleItems();
  }, [selectedSteelOrder]);

  if (!selectedSteelOrder) return null;

  const handleToggleItem = (index: number) => {
    setItems(prev => {
      const next = [...prev];
      next[index] = { ...next[index], selected: !next[index].selected };
      return next;
    });
  };

  const handleQtdChange = (index: number, value: string) => {
    setItems(prev => {
      const next = [...prev];
      next[index] = { ...next[index], qtdNF: value };
      return next;
    });
  };

  const handleValorChange = (index: number, value: string) => {
    setItems(prev => {
      const next = [...prev];
      next[index] = { ...next[index], valorNF: value };
      return next;
    });
  };

  const handleVarianteQtdChange = (index: number, key: string, value: string) => {
    setItems(prev => {
      const next = [...prev];
      next[index] = {
        ...next[index],
        varianteQtds: { ...next[index].varianteQtds, [key]: value },
      };
      return next;
    });
  };

  // Soma das quantidades por variante já digitadas para o item (regra 6 é validada pelo
  // backend contra a grade; aqui só o total do item contra quantidadeDisponivel).
  const totalVariantesSelecionadas = (item: any): number =>
    Object.values(item.varianteQtds || {}).reduce(
      (sum: number, v) => sum + (Number(v) || 0),
      0,
    );

  const handleConfirm = async () => {
    const selectedItems = items.filter(item => item.selected);

    if (selectedItems.length === 0) {
      setFeedback({ message: 'Selecione ao menos um item e informe uma quantidade válida.', isOpen: true, statusOk: false });
      return;
    }

    const semGrade = selectedItems.filter(item => !(item.grade?.length > 0));
    const comGrade = selectedItems.filter(item => item.grade?.length > 0);

    const incompleto = semGrade.some(item =>
      !(Number(item.qtdNF) > 0) ||
      !Number.isInteger(Number(item.qtdNF)) ||
      !(Number(item.valorNF) > 0)
    );

    if (incompleto) {
      setFeedback({ message: 'Informe quantidade inteira e valor unitário válidos para os itens selecionados.', isOpen: true, statusOk: false });
      return;
    }

    // Grade (T11): total selecionado do item validado contra quantidadeDisponivel; cada
    // variante validada contra a quantidade da própria grade (a regra 6 do backend é a
    // fonte de verdade — isto só evita uma ida e volta óbvia ao servidor).
    const gradeIncompleto = comGrade.some(item => {
      if (!(Number(item.valorNF) > 0)) return true;
      const total = totalVariantesSelecionadas(item);
      if (!(total > 0) || !Number.isInteger(total)) return true;
      if (total > item.quantidadeDisponivel) return true;
      return item.grade.some((g: any) => {
        const qtd = Number(item.varianteQtds[varianteKey(g.collectionColorId, g.size)] || 0);
        return qtd < 0 || !Number.isInteger(qtd) || qtd > g.quantidade;
      });
    });

    if (gradeIncompleto) {
      setFeedback({ message: 'Informe quantidades válidas por variante — o total do item não pode passar do saldo disponível.', isOpen: true, statusOk: false });
      return;
    }

    try {
      const linhasSemGrade = semGrade.map(item => {
        const quantidade = Math.floor(Number(item.qtdNF));
        const valorUnitario = round4(Number(item.valorNF));
        return {
          orderItemId: item.id,
          quantidade,
          valorUnitario,
          valorTotal: round4(quantidade * valorUnitario),
          ...(item.unitOfMeasurement?.id != null ? { unitOfMeasurementId: item.unitOfMeasurement.id } : {}),
        };
      });

      // valorUnitario uniforme entre as variantes do item (D-F3) — mesmo item.valorNF em
      // toda linha, uma por variante com quantidade > 0.
      const linhasComGrade = comGrade.flatMap(item => {
        const valorUnitario = round4(Number(item.valorNF));
        return item.grade
          .map((g: any) => ({
            g,
            quantidade: Math.floor(Number(item.varianteQtds[varianteKey(g.collectionColorId, g.size)] || 0)),
          }))
          .filter(({ quantidade }: any) => quantidade > 0)
          .map(({ g, quantidade }: any) => ({
            orderItemId: item.id,
            collectionColorId: g.collectionColorId,
            size: g.size,
            quantidade,
            valorUnitario,
            valorTotal: round4(quantidade * valorUnitario),
            ...(item.unitOfMeasurement?.id != null ? { unitOfMeasurementId: item.unitOfMeasurement.id } : {}),
          }));
      });

      const payload = {
        orderId: selectedSteelOrder.id,
        items: [...linhasSemGrade, ...linhasComGrade],
      };

      const { data: invoice } = await ApiService.fiscal.createNfe(payload);

      setFeedback({ message: 'Nota Fiscal gerada com sucesso!', isOpen: true, statusOk: true });
      setModalActive(false);
      navigate(`/panel/invoice/edit/${invoice.id}`);
    } catch (error) {
      console.error('❌ Erro ao gerar NF:', error);
      setFeedback({ message: 'Houve um erro ao tentar gerar a NF.', isOpen: true, statusOk: false });
    }
  };

  const companyName = typeof selectedSteelOrder.client === 'string'
    ? selectedSteelOrder.client
    : (selectedSteelOrder.client?.fantasyName || selectedSteelOrder.client?.companyName || 'Cliente Desconhecido');

  return (
    <ModalContainer>
      <Title>Pedido {selectedSteelOrder.id}</Title>
      <Subtitle>Cliente: {companyName}</Subtitle>

      <TableTitle>Itens do pedido</TableTitle>

      <Table>
        <thead>
          <tr>
            <Th>Adicionar</Th>
            <Th>Item</Th>
            <Th>NCM</Th>
            <Th>Unidade</Th>
            <Th>Valor unit.</Th>
            <Th>Qtd disponível</Th>
            <Th>Qtd para NF</Th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, index) => (
            <React.Fragment key={index}>
            <ItemRow $inelegivel={item.elegivel === false || item.totalmenteFaturado === true}>
              <Td>
                <CheckboxInput
                  type="checkbox"
                  checked={item.selected}
                  onChange={() => handleToggleItem(index)}
                  disabled={item.elegivel === false || item.totalmenteFaturado === true}
                />
              </Td>
              <Td>
                {item.totalmenteFaturado === true ? (
                  <DescricaoRiscada>{stripHtml(item.descricao) || `Produto ${item.id || index}`}</DescricaoRiscada>
                ) : (
                  stripHtml(item.descricao) || `Produto ${item.id || index}`
                )}
                {(item.pendencias ?? []).length > 0 && (
                  <BadgesRow>
                    {item.pendencias.map((p: any) => (
                      <BadgePendencia key={p.label} $bloqueante={p.bloqueante} title={p.descricao}>
                        {p.label}
                      </BadgePendencia>
                    ))}
                  </BadgesRow>
                )}
                {item.totalmenteFaturado === true && (
                  <MotivoInelegibilidade>Item já faturado — sem saldo disponível</MotivoInelegibilidade>
                )}
              </Td>
              <Td>{item.ncm || '—'}</Td>
              <Td>{item.unitOfMeasurement?.name || '—'}</Td>
              <Td>
                {item.valorUnitarioSugerido != null ? (
                  <ValorFixo title="Valor da venda — ajustes só na edição da nota">
                    {item.valorNF}
                  </ValorFixo>
                ) : (
                  <ValorInput
                    type="number"
                    value={item.valorNF}
                    onChange={(e) => handleValorChange(index, e.target.value)}
                    disabled={!item.selected}
                    min="0"
                    step="0.0001"
                  />
                )}
              </Td>
              <Td>{item.quantidadeDisponivel}</Td>
              <Td>
                {item.grade?.length > 0 ? (
                  <GradeTotal $excedido={totalVariantesSelecionadas(item) > item.quantidadeDisponivel}>
                    {totalVariantesSelecionadas(item)} / {item.quantidadeDisponivel}
                  </GradeTotal>
                ) : (
                  <QtdInput
                    type="number"
                    value={item.selected ? item.qtdNF : ''}
                    onChange={(e) => handleQtdChange(index, e.target.value)}
                    disabled={!item.selected}
                    min="1"
                    step="1"
                    max={item.quantidadeDisponivel}
                  />
                )}
              </Td>
            </ItemRow>
            {item.grade?.length > 0 && item.selected && (
              <GradeRow key={`${index}-grade`}>
                <GradeCell colSpan={7}>
                  <GradeTable>
                    <thead>
                      <tr>
                        <GradeVariantCell as="th">Cor</GradeVariantCell>
                        <GradeVariantCell as="th">Tamanho</GradeVariantCell>
                        <GradeVariantCell as="th">Qtd na grade</GradeVariantCell>
                        <GradeVariantCell as="th">Qtd para NF</GradeVariantCell>
                      </tr>
                    </thead>
                    <tbody>
                      {item.grade.map((g: any) => {
                        const key = varianteKey(g.collectionColorId, g.size);
                        return (
                          <GradeVariantRow key={key}>
                            <GradeVariantCell>{g.cor}</GradeVariantCell>
                            <GradeVariantCell>{g.size}</GradeVariantCell>
                            <GradeVariantCell>{g.quantidade}</GradeVariantCell>
                            <GradeVariantCell>
                              <QtdInput
                                type="number"
                                value={item.varianteQtds[key] ?? ''}
                                onChange={(e) => handleVarianteQtdChange(index, key, e.target.value)}
                                min="0"
                                step="1"
                                max={g.quantidade}
                              />
                            </GradeVariantCell>
                          </GradeVariantRow>
                        );
                      })}
                    </tbody>
                  </GradeTable>
                </GradeCell>
              </GradeRow>
            )}
            </React.Fragment>
          ))}
          {items.length === 0 && (
            <tr>
              <Td colSpan={7}>Nenhum item encontrado neste pedido.</Td>
            </tr>
          )}
        </tbody>
      </Table>

      {items.some((i) => (i.pendencias ?? []).length > 0) && (
        <Legenda>
          <LegendaItem><LegendaDot $bloqueante /> impede faturar</LegendaItem>
          <LegendaItem><LegendaDot /> pendência para a emissão</LegendaItem>
        </Legenda>
      )}

      <ActionsContainer>
        <CancelButton onClick={() => setModalActive(false)}>Cancelar</CancelButton>
        <ConfirmButton onClick={handleConfirm} disabled={items.filter(i => i.selected).length === 0}>Gerar NF</ConfirmButton>
      </ActionsContainer>
    </ModalContainer>
  );
};

export default GenerateOrderInvoiceModal;
